import { useEffect, useState } from "react";
import { GamePanelProps } from "../ui/GameContent";
import { WordSelectionItem } from "../../types/game";
import { shuffle } from "../../utils/utils";
import { QuestionItem } from "../game/QuestionItem";
import useGameStore from "../../context/GameContext";
import { CompletionModal } from "../ui/CompletionModal";
import "./SequentialMode.css";

export const SequentialMode = ({ items, libraryName }: GamePanelProps) => {
  const { getFormattedTime, gameId } = useGameStore();
  // 使用状态来跟踪每个问题的选择
  const [selectedOptions, setSelectedOptions] = useState<{
    [key: number]: string;
  }>({});
  // 将状态改为Map，使用word作为键
  const [wrongAnswersMap, setWrongAnswersMap] = useState<
    Map<string, WordSelectionItem>
  >(new Map());

  const [shuffledQuestions, setShuffledQuestions] = useState<
    WordSelectionItem[]
  >([]);
  const [submittedAnswers, setSubmittedAnswers] = useState(false);
  const [currentIndex, setCurrentIndex] = useState<number>(0); // 设置初始值为0而不是null
  // 临时消息和结果相关状态
  const [tempMessage, setTempMessage] = useState<{
    text: string;
    color: string;
  } | null>(null);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [completionTime, setCompletionTime] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false); // 添加一个初始化标志

  // 初始化问题和选项
  useEffect(() => {
    const initGame = () => {
      setSelectedOptions({});
      setWrongAnswersMap(new Map());
      setShowCompletionModal(false);

      const shuffled = shuffle([...items]);
      const questionsWithShuffledOptions = shuffled.map(
        (question) =>
          ({
            ...question,
            options: shuffle(question.options),
          } as WordSelectionItem)
      );
      setShuffledQuestions(questionsWithShuffledOptions);
      setCurrentIndex(0);
      setIsInitialized(true); // 标记初始化完成
    };

    initGame();
  }, [items, gameId]);

  // 显示临时消息的函数
  const showTempMessage = (message: string, color: string) => {
    setTempMessage({ text: message, color });
    setTimeout(() => setTempMessage(null), 3000);
  };

  // 移除特定索引的选项
  const removeSelectedOption = (indexToRemove: number) => {
    const { [indexToRemove]: _, ...remainingOptions } = selectedOptions;
    setSelectedOptions(remainingOptions);
  };

  // 添加错误答案的函数
  const addUniqueWrongAnswer = (item: WordSelectionItem) => {
    setWrongAnswersMap((prev) => {
      const newMap = new Map(prev);
      newMap.set(item.word, item);
      return newMap;
    });
  };

  const checkAnswer = (choice: string) => {
    if (
      currentIndex >= shuffledQuestions.length ||
      !shuffledQuestions[currentIndex]
    ) {
      return; // 防止数组越界
    }

    const question = shuffledQuestions[currentIndex];
    if (choice === question.correctAnswer) {
      showTempMessage("回答正确！", "#28a745");

      // 检查是否完成所有问题
      if (currentIndex + 1 >= shuffledQuestions.length) {
        // 游戏完成
        // 计算时间（将格式化的时间转换为秒）
        const timeStr = getFormattedTime();
        const timeParts = timeStr.split(":");
        let totalSeconds = 0;

        if (timeParts.length === 2) {
          // MM:SS 格式
          totalSeconds = parseInt(timeParts[0]) * 60 + parseInt(timeParts[1]);
        } else if (timeParts.length === 3) {
          // HH:MM:SS 格式
          totalSeconds =
            parseInt(timeParts[0]) * 3600 +
            parseInt(timeParts[1]) * 60 +
            parseInt(timeParts[2]);
        }

        setCompletionTime(totalSeconds);
        setShowCompletionModal(true);
        useGameStore.getState().pauseTimer();
      } else {
        setCurrentIndex(currentIndex + 1);
      }
    } else {
      showTempMessage("回答错误！请重试。", "#dc3545");
      removeSelectedOption(currentIndex);
      addUniqueWrongAnswer(question);
    }
  };

  const handleOptionClick = (questionIndex: number, optionValue: string) => {
    if (submittedAnswers) return; // 如果已经提交，禁止更改选择

    // 更新状态，记录哪个问题选了哪个答案
    setSelectedOptions((prev) => ({
      ...prev,
      [questionIndex]: optionValue,
    }));
    checkAnswer(optionValue);
  };

  // 重置游戏状态
  const handlePlayAgain = () => {
    setSelectedOptions({});
    setWrongAnswersMap(new Map());
    setShowCompletionModal(false);

    const shuffled = shuffle([...items]);
    const questionsWithShuffledOptions = shuffled.map(
      (question) =>
        ({
          ...question,
          options: shuffle(question.options),
        } as WordSelectionItem)
    );
    setShuffledQuestions(questionsWithShuffledOptions);
    setCurrentIndex(0);

    // 重置并启动计时器
    useGameStore.getState().resetTimer();
    useGameStore.getState().startTimer();
  };

  // 关闭模态框
  const handleCloseModal = () => {
    setShowCompletionModal(false);
  };

  // 如果还没有问题或当前索引无效，显示加载或默认内容
  if (
    !isInitialized ||
    shuffledQuestions.length === 0 ||
    currentIndex >= shuffledQuestions.length
  ) {
    return <div className="loading">Loading questions...</div>;
  }

  return (
    <div className="sequential-mode-container">
      {/* 临时消息显示 */}
      {tempMessage && (
        <div
          className="temp-message"
          style={{ backgroundColor: tempMessage.color }}
        >
          {tempMessage.text}
        </div>
      )}

      {/* 完成后的模态框 */}
      {showCompletionModal && (
        <CompletionModal
          time={completionTime}
          missedWords={Array.from(wrongAnswersMap.values())}
          onClose={handleCloseModal}
          onPlayAgain={handlePlayAgain}
          mode="sequential"
        />
      )}

      {/* 确保当前问题存在后再渲染 */}
      <QuestionItem
        question={shuffledQuestions[currentIndex]}
        index={currentIndex}
        selectedOptions={selectedOptions}
        submittedAnswers={submittedAnswers}
        onSelectOption={handleOptionClick}
      />
    </div>
  );
};
